import { watch } from 'vue'
import {
  defaultAccountPrefs,
  type AccountCollectionClaimed,
  type AccountPrefs,
  type BanlistLevelReward,
  type AccountLevelSnapshot,
  type CollectionState,
  type DailyMissionsSnapshot,
  type LastCallSnapshot,
  type LeagueId,
  type ProfileShowcaseSlot,
  type SavedDeckSlot,
} from '@shared/index'
import { api, apiUrl, ApiError } from '../api/http'
import { pullInterruptNotice } from './interruptNotice'
import { pullBugRewardNotice } from './bugRewardNotice'
import { clearStaffGrantNotice, pullStaffGrantNotice } from './staffGrantNotice'
import { clearPlayerMail, pullPlayerMail } from './playerMail'
import { pullSeasonNotice } from './seasonNotice'
import { noteClientDevice } from './device'
import { useAuthStore } from '../stores/auth'
import { useCollectionStore } from '../stores/collection'
import { useDeckStore } from '../stores/deck'
import { useWalletStore } from '../stores/wallet'
import { useRankedLobbyStore } from '../stores/rankedLobby'
import { useProgressStore } from '../stores/progress'
import { useBanlistRewardStore } from '../stores/banlistReward'
import { useMissionsStore } from '../stores/missions'
import { useLastCallStore } from '../stores/lastCall'
import { useLeagueRewardsStore } from '../stores/leagueRewards'
import { useChatMuteStore } from '../stores/chatMutes'
import { useChatStore } from '../stores/chat'
import { useFriendsStore } from '../stores/friends'
import { useThemeStore } from '../stores/theme'
import { useLocaleStore } from '../stores/locale'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { usePlayerStore } from '../stores/player'
import {
  activateFirstPlayable,
  deflateDeckSlot,
  inflateSavedDeck,
} from '../game/localDeck'

export type AccountStatePayload = {
  coins: number
  collection: CollectionState
  decks: SavedDeckSlot[]
  activeDeckId: string | null
  prefs: AccountPrefs
  progress: AccountLevelSnapshot
  showcase?: ProfileShowcaseSlot[]
  missions?: DailyMissionsSnapshot
  rankedNudge?: { pending: number }
  rankedCallClaims?: number
  lastCall?: LastCallSnapshot
  leaguePeak?: LeagueId | null
  deckWrittenAt?: number
  banlistReward?: BanlistLevelReward
} & Partial<AccountCollectionClaimed>

function dropLegacyLocal(): void {
  try {
    localStorage.removeItem('collection')
    localStorage.removeItem('deck')
    localStorage.removeItem('player')
  } catch {
    /* ignore */
  }
}

let hydratedToken: string | null = null
let inflight: Promise<void> | null = null
let applying = false
let deckTimer: ReturnType<typeof setTimeout> | null = null
let prefsTimer: ReturnType<typeof setTimeout> | null = null
let prefsAbort: AbortController | null = null
/** Bumped on each local edit and on logout so a late PATCH cannot clear a newer edit. */
let prefsEpoch = 0
let watching = false

/**
 * Set while this tab has a sleeve/mat/theme edit the server has not acknowledged.
 * Survives a reload so hydrate cannot put the old sleeve back.
 */
const PREFS_DIRTY_KEY = 'dc-prefs-dirty'

/**
 * One page load. A later write from this page can still land after an earlier
 * one; a different page must have seen the latest stamp or the server rejects it.
 */
let deckSession = newDeckSessionId()
let sessionEpoch = 0
let nextDeckSeq = 0
let newestSentSeq = 0
let baseWrittenAt = 0
let editToken = 0
let pendingToken = 0
let rejectedToken = 0
let deckWrites = 0
let deckQueued = false
let deckChain: Promise<void> = Promise.resolve()
let deckRefresh: Promise<void> | null = null
let lastDeckSyncAt = 0

type DeckSyncBody = {
  decks?: SavedDeckSlot[]
  activeDeckId?: string | null
  deckWrittenAt?: number
}

function newDeckSessionId(): string {
  const web = globalThis.crypto
  if (web && typeof web.randomUUID === 'function') return web.randomUUID()
  return `session-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

function rotateDeckSession(): void {
  deckSession = newDeckSessionId()
  sessionEpoch += 1
  nextDeckSeq = 0
  newestSentSeq = 0
}

function asDeckSyncBody(raw: unknown): DeckSyncBody | null {
  if (!raw || typeof raw !== 'object') return null
  const body = raw as DeckSyncBody
  if (!Array.isArray(body.decks) || typeof body.deckWrittenAt !== 'number') return null
  return body
}

/**
 * Replaces local barajas with a server snapshot. Sync watchers stay quiet.
 */
/**
 * Quietly replaces local barajas with the lists recycled on the server.
 */
export function applyRecycledDecks(raw: unknown): void {
  applyServerDecks(raw, false)
}

function applyServerDecks(raw: unknown, rotate: boolean): boolean {
  const body = asDeckSyncBody(raw)
  if (!body || !body.decks) return false
  applying = true
  try {
    if (rotate) rotateDeckSession()
    useDeckStore().hydrateFromServer(
      body.decks.map(inflateSavedDeck),
      body.activeDeckId ?? null,
    )
    baseWrittenAt = body.deckWrittenAt ?? baseWrittenAt
    pendingToken = 0
    rejectedToken = 0
  } finally {
    applying = false
  }
  return true
}

function applyPrefs(prefs: AccountPrefs): void {
  useLocaleStore().setLocale(prefs.locale)
  useThemeStore().setAccent(prefs.accentHex)
  useThemeStore().setScheme(prefs.colorScheme)
  const duel = useDuelPrefsStore()
  duel.setVsAiDice(prefs.vsAiDice)
  duel.setVsAiFirst(prefs.vsAiFirst)
  duel.setVsAiDifficulty(prefs.vsAiDifficulty)
  duel.setMatId(prefs.matId)
  duel.setMatOpacity(prefs.matOpacity)
  duel.setFollowOpponentViewer(prefs.followOpponentViewer)
  duel.setFocusBands(prefs.focusBands)
  duel.setSleeveId(prefs.sleeveId)
  duel.setRankedPrompts(prefs.rankedPrompts !== false)
}

function currentPrefs(): AccountPrefs {
  const locale = useLocaleStore()
  const theme = useThemeStore()
  const duel = useDuelPrefsStore()
  return {
    locale: locale.locale,
    accentHex: theme.accentHex,
    colorScheme: theme.colorScheme,
    vsAiDice: duel.vsAiDice,
    vsAiFirst: duel.vsAiFirst,
    vsAiDifficulty: duel.vsAiDifficulty,
    matId: duel.matId,
    matOpacity: duel.matOpacity,
    followOpponentViewer: duel.followOpponentViewer,
    focusBands: duel.focusBands,
    sleeveId: duel.sleeveId,
    rankedPrompts: duel.rankedPrompts,
  }
}

/**
 * Clears album, barajas, and prefs in this browser (logout / other account).
 */
export function resetAccountLocal(): void {
  hydratedToken = null
  rotateDeckSession()
  pendingToken = 0
  rejectedToken = 0
  baseWrittenAt = 0
  lastDeckSyncAt = 0
  if (deckTimer) clearTimeout(deckTimer)
  deckTimer = null
  invalidatePrefsWrite()
  applying = true
  const keptLocale = useLocaleStore().locale
  try {
    dropLegacyLocal()
    clearStaffGrantNotice()
    clearPlayerMail()
    useWalletStore().clear()
    useRankedLobbyStore().clear()
    useProgressStore().clear()
    useBanlistRewardStore().clear()
    useMissionsStore().clear()
    useLeagueRewardsStore().clear()
    useChatMuteStore().clear()
    useFriendsStore().clear()
    useChatStore().clearDirect()
    useCollectionStore().reset()
    useDeckStore().clearDeck()
    usePlayerStore().reset()
    applyPrefs(defaultAccountPrefs())
    useLocaleStore().setLocale(keptLocale)
  } finally {
    applying = false
  }
}

/**
 * Loads coins, collection, decks, and prefs for the signed-in account.
 */
/**
 * DevTools: wipe the signed-in account on the server, then re-hydrate.
 */
export async function resetAccountRemote(password: string): Promise<boolean> {
  const auth = useAuthStore()
  if (!auth.token) return false
  try {
    await api('/account/dev-reset', {
      method: 'POST',
      token: auth.token,
      body: JSON.stringify({ password }),
    })
    hydratedToken = null
    await hydrateAccount(true)
    return true
  } catch {
    return false
  }
}

/**
 * Loads coins, collection, decks, and prefs for the signed-in account.
 */
export async function hydrateAccount(force = false): Promise<void> {
  const auth = useAuthStore()
  if (!auth.token) {
    resetAccountLocal()
    return
  }
  if (!force && hydratedToken === auth.token) return
  if (inflight !== null) return inflight
  const collection = useCollectionStore()
  const epochAtFetch = collection.mutationEpoch()
  inflight = (async () => {
    try {
      await settleDeckWrites()
      const state = await api<AccountStatePayload>('/account/state', {
        token: auth.token,
      })
      applying = true
      try {
        useWalletStore().setCoins(state.coins)
        useRankedLobbyStore().setOfferPending(state.rankedNudge?.pending ?? 0)
        useRankedLobbyStore().setCallClaims(state.rankedCallClaims ?? 0)
        if (state.progress) useProgressStore().applySnapshot(state.progress)
        if (state.banlistReward) useBanlistRewardStore().apply(state.banlistReward)
        useMissionsStore().setCollectionClaimed(state.collectionClaimed ?? [])
        const collectionStale = collection.mutationEpoch() !== epochAtFetch
        if (!collectionStale) collection.applyState(state.collection)
        if (state.lastCall) {
          useLastCallStore().apply(
            collectionStale
              ? { ...state.lastCall, packs: undefined }
              : state.lastCall,
          )
        }
        if (state.missions && state.missions.rows.length > 0) {
          useMissionsStore().apply(state.missions)
        }
        useLeagueRewardsStore().apply(state.leaguePeak)
        rotateDeckSession()
        useDeckStore().hydrateFromServer(
          state.decks.map(inflateSavedDeck),
          state.activeDeckId,
        )
        if (typeof state.deckWrittenAt === 'number') {
          baseWrittenAt = state.deckWrittenAt
        }
        pendingToken = 0
        rejectedToken = 0
        lastDeckSyncAt = Date.now()
        // A sleeve picked in the duel may still be on the way to the server.
        // Applying this snapshot would put the previous sleeve back.
        if (!prefsNeedPush()) applyPrefs(state.prefs)
        usePlayerStore().setShowcase(state.showcase ?? [])
        dropLegacyLocal()
        hydratedToken = auth.token
      } finally {
        applying = false
      }
      if (prefsNeedPush()) sendPrefs(false)
      void pullInterruptNotice()
      void pullBugRewardNotice()
      void pullStaffGrantNotice()
      void pullPlayerMail()
      void pullSeasonNotice()
      noteClientDevice(auth.token)
      activateFirstPlayable()
    } catch (err) {
      hydratedToken = null
      if (err instanceof ApiError && err.status === 401) {
        resetAccountLocal()
        auth.logout()
      }
    }
  })().finally(() => {
    inflight = null
  })
  return inflight
}

/**
 * Finishes a baraja write already queued so a reload, or leaving the editor,
 * cannot snapshot the list before that write lands.
 */
export async function settleDeckWrites(): Promise<void> {
  if (!hydratedToken) return
  for (let attempt = 0; attempt < 4; attempt++) {
    if (deckTimer) {
      clearTimeout(deckTimer)
      deckTimer = null
    }
    if (pendingToken !== 0) enqueueDeckWrite()
    await deckChain
    if (pendingToken === 0 && deckWrites === 0 && !deckQueued) return
  }
}

function deckLocalDirty(): boolean {
  return pendingToken !== 0 || deckTimer !== null || deckWrites > 0
}

function armDeckWrite(delay: number): void {
  if (deckTimer) clearTimeout(deckTimer)
  deckTimer = setTimeout(() => {
    deckTimer = null
    enqueueDeckWrite()
  }, delay)
}

function enqueueDeckWrite(): void {
  if (deckQueued) return
  deckQueued = true
  deckChain = deckChain
    .then(async () => {
      deckQueued = false
      await persistDecksNow()
    })
    .catch(() => {
      deckQueued = false
    })
}

function deckPutBody(seq: number, base: number, session: string): string {
  const deck = useDeckStore()
  deck.ensureIntegrity()
  return JSON.stringify({
    decks: deck.decks.map(deflateDeckSlot),
    activeDeckId: deck.activeId,
    baseDeckWrittenAt: base,
    deckSession: session,
    deckSeq: seq,
  })
}

/**
 * A second PUT that can finish after the page is already in the background.
 * Used when another write is still on the wire with an older list.
 */
function sendDeckKeepalive(): void {
  const auth = useAuthStore()
  if (!auth.token || pendingToken === 0) return
  const seq = ++nextDeckSeq
  newestSentSeq = seq
  const body = deckPutBody(seq, baseWrittenAt, deckSession)
  void fetch(apiUrl('/decks'), {
    method: 'PUT',
    keepalive: true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth.token}`,
    },
    body,
  }).catch(() => {
    /* the queued write retries if this page stays alive */
  })
}

/**
 * Sends the latest local list. keepalive so a phone can background mid-request.
 */
async function persistDecksNow(): Promise<void> {
  if (pendingToken === 0 || applying) return
  const auth = useAuthStore()
  if (!auth.token || hydratedToken !== auth.token) return
  const epoch = sessionEpoch
  const seq = ++nextDeckSeq
  newestSentSeq = seq
  const tokenAtSend = pendingToken
  const sessionAtSend = deckSession
  const baseAtSend = baseWrittenAt
  const sentToken = auth.token
  const body = deckPutBody(seq, baseAtSend, sessionAtSend)
  deckWrites += 1
  let followDelay = 0
  try {
    if (
      epoch !== sessionEpoch ||
      pendingToken === 0 ||
      useAuthStore().token !== sentToken
    ) {
      return
    }
    const saved = await api<DeckSyncBody>('/decks', {
      method: 'PUT',
      keepalive: true,
      token: sentToken,
      body,
    })
    if (epoch !== sessionEpoch || seq < newestSentSeq) return
    if (useAuthStore().token !== sentToken) return
    if (typeof saved.deckWrittenAt === 'number') baseWrittenAt = saved.deckWrittenAt
    if (pendingToken === tokenAtSend) pendingToken = 0
    lastDeckSyncAt = Date.now()
    if (pendingToken !== 0) followDelay = 400
  } catch (err) {
    if (epoch !== sessionEpoch || seq < newestSentSeq) return
    if (!(err instanceof ApiError)) {
      followDelay = 2000
      return
    }
    if (err.status === 401 && useAuthStore().token === sentToken) {
      resetAccountLocal()
      useAuthStore().logout()
      return
    }
    if (err.status === 409 && (err.code === 'deck_stale' || err.code === 'deck_locked')) {
      applyServerDecks(err.payload, true)
      return
    }
    if (err.status === 400) {
      rejectedToken = tokenAtSend
      if (pendingToken === tokenAtSend) pendingToken = 0
      else followDelay = 400
      return
    }
    followDelay = 2000
  } finally {
    deckWrites -= 1
    if (
      followDelay > 0 &&
      epoch === sessionEpoch &&
      pendingToken !== 0 &&
      pendingToken !== rejectedToken
    ) {
      armDeckWrite(followDelay)
    }
  }
}

/**
 * Drops the debounce and writes now. Mobile browsers freeze timers in the background.
 */
function flushDeckPersist(): void {
  if (!useAuthStore().token || !hydratedToken || pendingToken === 0) return
  if (deckTimer) {
    clearTimeout(deckTimer)
    deckTimer = null
  }
  // fetch() has to start inside the hide handler. A queued promise can run
  // after the phone has already frozen the page.
  sendDeckKeepalive()
  enqueueDeckWrite()
}

function editorOrDuelOpen(): boolean {
  if (typeof location === 'undefined') return false
  const path = location.pathname
  return (
    path.startsWith('/online-duel') ||
    path === '/duel' ||
    /^\/deck-builder\/.+/.test(path)
  )
}

/**
 * Pulls the account's barajas when this page has no local edit waiting.
 */
export function refreshDecksIfClean(): Promise<void> {
  const auth = useAuthStore()
  if (!auth.token || hydratedToken !== auth.token) return Promise.resolve()
  if (applying || deckLocalDirty()) return Promise.resolve()
  if (Date.now() - lastDeckSyncAt < 1500) return Promise.resolve()
  if (deckRefresh) return deckRefresh
  const epoch = sessionEpoch
  const tokenAtSend = auth.token
  const activeAtFetch = useDeckStore().activeId
  deckRefresh = (async () => {
    try {
      const snap = await api<DeckSyncBody>('/decks', {
        token: tokenAtSend,
        cache: 'no-store',
        quiet: true,
      })
      if (epoch !== sessionEpoch || useAuthStore().token !== tokenAtSend) return
      if (applying || deckLocalDirty()) return
      if (typeof snap.deckWrittenAt === 'number' && snap.deckWrittenAt === baseWrittenAt) {
        lastDeckSyncAt = Date.now()
        return
      }
      // A choice made while this GET was in flight is newer than the snapshot.
      if (useDeckStore().activeId !== activeAtFetch) return
      if (!applyServerDecks(snap, false)) return
      lastDeckSyncAt = Date.now()
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 401 &&
        useAuthStore().token === tokenAtSend
      ) {
        resetAccountLocal()
        useAuthStore().logout()
      }
    }
  })().finally(() => {
    deckRefresh = null
  })
  return deckRefresh
}

function onDeckVisibility(): void {
  if (document.visibilityState === 'hidden') {
    flushDeckPersist()
    return
  }
  if (editorOrDuelOpen()) return
  void refreshDecksIfClean()
}

function onDeckFocus(): void {
  if (editorOrDuelOpen()) return
  void refreshDecksIfClean()
}

function prefsNeedPush(): boolean {
  try {
    return sessionStorage.getItem(PREFS_DIRTY_KEY) === '1'
  } catch {
    return false
  }
}

function markPrefsDirty(): void {
  prefsEpoch += 1
  try {
    sessionStorage.setItem(PREFS_DIRTY_KEY, '1')
  } catch {
    /* private mode */
  }
}

function clearPrefsDirty(): void {
  try {
    sessionStorage.removeItem(PREFS_DIRTY_KEY)
  } catch {
    /* ignore */
  }
}

/** Drop a queued or in-flight prefs write. Used on logout. */
function invalidatePrefsWrite(): void {
  prefsEpoch += 1
  if (prefsTimer) clearTimeout(prefsTimer)
  prefsTimer = null
  prefsAbort?.abort()
  prefsAbort = null
  clearPrefsDirty()
}

/**
 * PATCH the prefs currently in memory. `keepalive` so leaving the page
 * still delivers the sleeve chosen a moment earlier.
 */
function sendPrefs(keepalive: boolean): void {
  const auth = useAuthStore()
  if (!auth.token || applying || !prefsNeedPush()) return
  if (!keepalive) prefsAbort?.abort()
  const ac = keepalive ? null : new AbortController()
  if (ac) prefsAbort = ac
  const epoch = prefsEpoch
  const token = auth.token
  const body = JSON.stringify(currentPrefs())
  void api('/account/prefs', {
    method: 'PATCH',
    keepalive,
    ...(ac ? { signal: ac.signal } : {}),
    token,
    body,
  })
    .then(() => {
      if (ac?.signal.aborted || epoch !== prefsEpoch) return
      clearPrefsDirty()
    })
    .catch((err: unknown) => {
      if (ac?.signal.aborted || epoch !== prefsEpoch) return
      if (err instanceof ApiError) {
        if (err.status === 401 && useAuthStore().token === token) {
          resetAccountLocal()
          useAuthStore().logout()
        } else if (err.status === 400) {
          clearPrefsDirty()
        }
        return
      }
      if (prefsTimer) clearTimeout(prefsTimer)
      prefsTimer = setTimeout(() => {
        prefsTimer = null
        if (epoch === prefsEpoch) sendPrefs(false)
      }, 2000)
    })
}

/**
 * Starts the prefs PATCH inside a hide/unload handler. A timer would be frozen
 * or killed before the sleeve left this page.
 */
function flushPrefsPersist(): void {
  if (prefsTimer) {
    clearTimeout(prefsTimer)
    prefsTimer = null
  }
  if (!prefsNeedPush()) return
  prefsAbort?.abort()
  prefsAbort = null
  sendPrefs(true)
}

function onPrefsVisibility(): void {
  if (document.visibilityState === 'hidden') flushPrefsPersist()
}

/**
 * Debounced PUT of the current baraja slots.
 */
export function scheduleDeckPersist(): void {
  if (applying || !useAuthStore().token || hydratedToken !== useAuthStore().token) return
  pendingToken = ++editToken
  rejectedToken = 0
  armDeckWrite(400)
}

/**
 * Debounced PATCH of theme, locale, and duel prefs.
 */
export function schedulePrefsPersist(): void {
  if (applying || !useAuthStore().token) return
  markPrefsDirty()
  if (prefsTimer) clearTimeout(prefsTimer)
  prefsTimer = setTimeout(() => {
    prefsTimer = null
    sendPrefs(false)
  }, 400)
}

/**
 * Watches local stores and writes them to the account. Call once from App.
 */
export function startAccountSync(): void {
  if (watching) return
  watching = true
  const deck = useDeckStore()
  const theme = useThemeStore()
  const locale = useLocaleStore()
  const duel = useDuelPrefsStore()
  watch(
    () => [deck.decks, deck.activeId] as const,
    () => {
      if (applying) return
      scheduleDeckPersist()
    },
    { deep: true, flush: 'sync' },
  )
  document.addEventListener('visibilitychange', onDeckVisibility)
  document.addEventListener('visibilitychange', onPrefsVisibility)
  window.addEventListener('pagehide', flushDeckPersist)
  window.addEventListener('pagehide', flushPrefsPersist)
  window.addEventListener('focus', onDeckFocus)
  window.setInterval(() => {
    if (document.visibilityState !== 'visible') return
    if (editorOrDuelOpen()) return
    void refreshDecksIfClean()
  }, 5000)
  watch(
    () => [
      locale.locale,
      theme.accentHex,
      theme.colorScheme,
      duel.vsAiDice,
      duel.vsAiFirst,
      duel.vsAiDifficulty,
      duel.matId,
      duel.matOpacity,
      duel.followOpponentViewer,
      duel.focusBands,
      duel.rankedPrompts,
      duel.sleeveId,
    ],
    () => schedulePrefsPersist(),
    // Sync so a hydrate's applyPrefs is still inside `applying` and does not
    // mark the server snapshot as a local edit.
    { flush: 'sync' },
  )
}
